import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { Usuario, AuthResponse, ApiResponse } from '../types';

const TOKEN_KEY = 'minotion_token';
const USER_KEY = 'minotion_user';

interface AuthContextType {
    usuario: Usuario | null;
    token: string | null;
    loading: boolean;
    error: string | null;
    login: (email: string, password: string) => Promise<boolean>;
    register: (nombre: string, email: string, password: string) => Promise<boolean>;
    logout: () => void;
    getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [usuario, setUsuario] = useState<Usuario | null>(() => {
        const stored = localStorage.getItem(USER_KEY);
        return stored ? JSON.parse(stored) : null;
    });
    const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getToken = useCallback((): string | null => token, [token]);

    const authFetch = useCallback(async (path: string, options: RequestInit = {}): Promise<Response> => {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...((options.headers as Record<string, string>) || {}),
        };
        const t = localStorage.getItem(TOKEN_KEY);
        if (t) headers['Authorization'] = `Bearer ${t}`;
        const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
        if (res.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            setToken(null);
            setUsuario(null);
        }
        return res;
    }, []);

    const login = useCallback(async (email: string, password: string): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const json: ApiResponse<AuthResponse> = await res.json();
            if (json.success && json.data) {
                localStorage.setItem(TOKEN_KEY, json.data.token);
                localStorage.setItem(USER_KEY, JSON.stringify(json.data.usuario));
                setToken(json.data.token);
                setUsuario(json.data.usuario);
                return true;
            }
            setError(json.error || 'Error al hacer login');
            return false;
        } catch (e) {
            setError('No se pudo conectar con el servidor');
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const register = useCallback(async (nombre: string, email: string, password: string): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nombre, email, password }),
            });
            const json: ApiResponse<AuthResponse> = await res.json();
            if (json.success && json.data) {
                localStorage.setItem(TOKEN_KEY, json.data.token);
                localStorage.setItem(USER_KEY, JSON.stringify(json.data.usuario));
                setToken(json.data.token);
                setUsuario(json.data.usuario);
                return true;
            }
            setError(json.error || 'Error al registrar');
            return false;
        } catch (e) {
            setError('No se pudo conectar con el servidor');
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUsuario(null);
    }, []);

    const verifyToken = useCallback(async () => {
        const t = localStorage.getItem(TOKEN_KEY);
        if (!t) return;
        try {
            const res = await fetch(`${API_BASE}/auth/me`, {
                headers: { 'Authorization': `Bearer ${t}` },
            });
            if (res.status === 401) {
                logout();
                return;
            }
            // Refresca el usuario cacheado con la versión fresca del
            // backend — no solo chequea que el token siga vivo. Si no se
            // hace esto, una sesión abierta desde antes de un cambio de
            // forma en Usuario (como agregar permisos_modulos hoy) se
            // queda para siempre con el objeto viejo en localStorage y
            // rompe cualquier componente que dependa del campo nuevo.
            const json: ApiResponse<{ usuario: Usuario }> = await res.json();
            if (json.success && json.data) {
                localStorage.setItem(USER_KEY, JSON.stringify(json.data.usuario));
                setUsuario(json.data.usuario);
            }
        } catch {
            // silent
        }
    }, [logout]);

    useEffect(() => {
        if (token) verifyToken();
    }, [token, verifyToken]);

    return (
        <AuthContext.Provider value={{ usuario, token, loading, error, login, register, logout, getToken }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
    return ctx;
};

export const getAuthToken = (): string | null => localStorage.getItem(TOKEN_KEY);